import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Modal, SparkModalPortal, sparkToast, ReadOnlyProvider, useReadOnly } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { data_from_server_post, data_from_server_delete } from '@/shared/http/client';
import { SdkDatasetExportControl } from '@/features/datasets/components/DatasetExportControl.jsx';
import { getUserFacingDatasetName, isSdkTraceDatasetName } from '@/features/datasets/utils/datasetCloneNames';

/**
 * User-facing dataset name in confirmation dialogs. Upload clones lose the
 * task suffix; SDK datasets use the dedicated traces/logs label.
 */
const getDisplayName = (name) => getUserFacingDatasetName(name) || name;

const isSdkDatasetRemoval = (names, exportContext) => {
    const candidates = [...(names || [])];
    if (exportContext?.datasetName) {
        candidates.push(exportContext.datasetName);
    }
    return candidates.some(isSdkTraceDatasetName);
};

const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * Title / body / primary-button copy for the delete confirmation modal.
 * Single dataset: `Delete <name> dataset?` with the name bold. SDK trace
 * datasets keep their dedicated permanent-trace-loss warning.
 */
const buildRemovalCopy = ({ names, isBenchmark, isSdk }) => {
    const displayNames = names.map(getDisplayName);
    const isSingle = names.length === 1;
    const label = isBenchmark ? 'benchmark' : 'dataset';

    const title = isSingle ? (
        <>
            Delete <strong>{displayNames[0]}</strong> {label}?
        </>
    ) : (
        `Delete ${names.length} ${label}s?`
    );
    const primaryLabel = `Delete ${capitalize(label)}${isSingle ? '' : 's'}`;

    let body;
    if (isSdk) {
        body = isSingle ? (
            <p className="spark-modal-text-content">
                Removing it will permanently delete all traced records that were collected
                through the SpecificAI SDK. Download a copy first if you want to keep the
                data.
            </p>
        ) : (
            <>
                <p className="spark-modal-text-content">
                    These SDK trace {label}s exist only on this task (not in the Datasets
                    library): {displayNames.join(', ')}.
                </p>
                <p className="spark-modal-text-content">
                    Removing them will permanently delete all traced records. Download
                    copies first if you want to keep the data.
                </p>
            </>
        );
    } else {
        body = (
            <p className="spark-modal-text-content">
                {isSingle
                    ? `All data associated with this ${label} will be permanently deleted. This action cannot be undone.`
                    : `All data associated with these ${label}s (${displayNames.join(', ')}) will be permanently deleted. This action cannot be undone.`}
            </p>
        );
    }

    return { title, primaryLabel, body };
};

/**
 * Mount the Spark delete-confirmation modal on its own React root under
 * document.body (same pattern as the task-delete dialog): the imperative
 * `confirmRemoval` API has no host JSX tree, and an independent root also
 * stacks above whichever modal (e.g. Add Data) triggered the removal.
 */
const openRemovalConfirmDialog = ({ title, body, primaryLabel, footerSlot, onConfirm }) => {
    const host = document.createElement('div');
    host.setAttribute('data-usecase-file-removal-dialog', '');
    document.body.appendChild(host);
    const root = createRoot(host);
    let disposed = false;

    const dispose = () => {
        disposed = true;
        root.unmount();
        host.remove();
    };

    // This root is outside the app's ReadOnlyProvider, so callers re-render it with the app's read-only state.
    const render = ({ readOnly, reason }) => {
        if (disposed) return;
        root.render(
            <ReadOnlyProvider readOnly={readOnly} reason={reason}>
                <SparkModalPortal open onClose={dispose} size="text">
                    <Modal
                        feedbackType="warning"
                        title={title}
                        showSubtitle={false}
                        showScrollBar={false}
                        bodyVariant="text"
                        danger
                        primaryLabel={primaryLabel}
                        primaryDisableWhenReadOnly
                        tertiaryLabel="Cancel"
                        showFooterSlot={Boolean(footerSlot)}
                        footerSlot={footerSlot}
                        onPrimaryClick={() => {
                            dispose();
                            onConfirm?.();
                        }}
                        onTertiaryClick={dispose}
                        onCloseClick={dispose}
                    >
                        {body}
                    </Modal>
                </SparkModalPortal>
            </ReadOnlyProvider>
        );
    };

    return render;
};

/**
 * Shared "remove dataset/benchmark from a task" plumbing. Consolidates the
 * confirmation modal + `set_datasets_and_benchmarks` mutation + per-clone
 * `DELETE /datasets` fan-out that previously lived inline in
 * `FromLibraryTab`. Reuse it from any UI that lets the user detach a
 * dataset/benchmark file from a task (e.g. the file tiles inside
 * `DataOverviewCard`).
 *
 * @param {Object} [options]
 * @param {() => void} [options.refreshLibraryOptions] Optional callback to
 *   re-fetch the library Select options after a successful removal (matches
 *   the legacy `FromLibraryTab` post-delete refresh).
 */
export const useUsecaseFileRemoval = ({ refreshLibraryOptions } = {}) => {
    const { refreshUsecases } = useContext(GlobalContext);
    const readOnlyState = useReadOnly();
    const readOnlyRef = useRef(readOnlyState);
    const renderOpenDialogRef = useRef(null);

    useEffect(() => {
        readOnlyRef.current = readOnlyState;
        renderOpenDialogRef.current?.(readOnlyState);
    }, [readOnlyState]);

    /**
     * Issue the server mutations to remove a list of names (datasets or
     * benchmarks) from a single usecase. Returns a Promise that resolves once
     * the bulk update + per-name DELETE fan-out completes (errors are logged
     * so the surrounding modal can still close cleanly).
     *
     * Accepts optional `currentDatasets`/`currentBenchmarks` overrides for
     * callers that track an in-flight, optimistic list separately from the
     * persisted `usecase.datasets`/`usecase.benchmarks` (e.g. the multi-Select
     * in `FromLibraryTab`).
     */
    const removeFromUsecase = useCallback(
        ({ usecase, namesToRemove, isBenchmark, currentDatasets, currentBenchmarks }) =>
            new Promise((resolve) => {
                if (!usecase || !namesToRemove?.length) {
                    resolve();
                    return;
                }
                const removeSet = new Set(namesToRemove);
                const baseDatasets = currentDatasets ?? usecase.datasets ?? [];
                const baseBenchmarks = currentBenchmarks ?? usecase.benchmarks ?? [];
                const newDatasets = isBenchmark
                    ? baseDatasets
                    : baseDatasets.filter((d) => !removeSet.has(d));
                const newBenchmarks = isBenchmark
                    ? baseBenchmarks.filter((b) => !removeSet.has(b))
                    : baseBenchmarks;

                data_from_server_post(
                    'set_datasets_and_benchmarks',
                    {
                        client_id: usecase.client_id,
                        llm_usecase_id: usecase._id,
                        datasets: newDatasets,
                        benchmarks: newBenchmarks,
                    },
                    () => {
                        let pending = namesToRemove.length;
                        const onDone = () => {
                            if (--pending <= 0) resolve();
                        };
                        namesToRemove.forEach((name) => {
                            data_from_server_delete(
                                'datasets',
                                {
                                    client_id: usecase.client_id,
                                    dataset: name,
                                    is_benchmark: isBenchmark,
                                    is_manual_model_predictions: false,
                                },
                                onDone,
                                (err) => {
                                    console.warn(
                                        'Clone data cleanup failed (may already be removed):',
                                        err
                                    );
                                    onDone();
                                }
                            );
                        });
                    },
                    (error) => {
                        sparkToast.error(
                            typeof error === 'string' ? error : 'Error removing dataset'
                        );
                        resolve();
                    }
                );
            }),
        []
    );

    const confirmRemoval = useCallback(
        ({ names, isBenchmark, onConfirm, exportContext = null }) => {
            if (!names?.length) return;
            const isSdk = isSdkDatasetRemoval(names, exportContext);
            const { title, primaryLabel, body } = buildRemovalCopy({
                names,
                isBenchmark,
                isSdk,
            });

            const footerSlot =
                isSdk && exportContext?.usecase && exportContext?.datasetName ? (
                    <SdkDatasetExportControl
                        exportContext={exportContext}
                        isBenchmark={exportContext?.isBenchmark ?? isBenchmark}
                    />
                ) : null;

            const renderDialog = openRemovalConfirmDialog({
                title,
                primaryLabel,
                body,
                footerSlot,
                onConfirm: async () => {
                    await onConfirm?.();
                    refreshUsecases();
                    refreshLibraryOptions?.();
                },
            });
            renderDialog(readOnlyRef.current);
            renderOpenDialogRef.current = renderDialog;
        },
        [refreshUsecases, refreshLibraryOptions]
    );

    return { confirmRemoval, removeFromUsecase };
};

export default useUsecaseFileRemoval;
